import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactHtmlParser from 'react-html-parser';
import themeGet from '@styled-system/theme-get';

import { Context } from '../../Providers/Blade';
import MenuItem from '../MenuItem';

import { Login, Menu, MenuButton, Play, StyledLink } from './styles';

const infoBarBreakpoint = 600;

export const InfoBar = styled(
  ({
    className,
    textHtml,
    ctaText,
    ctaUrl,
    ctaStyle,
    ctaText2,
    ctaUrl2,
    ctaStyle2,
    lang,
    foreground,
  }) => (
    <div className={className}>
      <div className="infobar-wrapper">
        {ReactHtmlParser(textHtml)}
        {/* based on request, when using 2 hyperlinks ensure they dont stack */}
        <div
          className={
            ctaStyle === 'hyperlink' && ctaStyle2 === 'hyperlink'
              ? 'hyperlink-wrapper'
              : 'cta-wrapper'
          }
        >
          {ctaStyle === 'hyperlink' ? (
            <StyledLink to={ctaUrl} foreground={foreground} lang={lang}>
              {ctaText}
            </StyledLink>
          ) : (
            <a href={ctaUrl}>
              <div className={ctaStyle}>
                <h3>{ctaText}</h3>
              </div>
            </a>
          )}
          {ctaText2 &&
            ctaUrl2 &&
            (ctaStyle2 === 'hyperlink' ? (
              <StyledLink to={ctaUrl2} foreground={foreground} lang={lang}>
                {ctaText2}
              </StyledLink>
            ) : (
              <a href={ctaUrl2}>
                <div className={ctaStyle2}>
                  <h3>{ctaText2}</h3>
                </div>
              </a>
            ))}
        </div>
      </div>
    </div>
  )
)`
  width: 100%;
  min-height: 50px;
  background-color: ${({ background }) =>
    background || themeGet('colors.white')};
  color: ${({ foreground }) => foreground || themeGet('colors.black')};
  display: flex;
  align-items: center;
  &.hide {
    display: none;
  }
  .infobar-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    .cta-wrapper,
    .hyperlink-wrapper {
      display: flex;
    }
    @media (max-width: ${infoBarBreakpoint}px) {
      flex-direction: column;
      padding: 10px 10px 0 10px;
      text-align: center;
      margin-top: 1rem;
      .hyperlink-wrapper {
        gap: 1.5rem;
      }
      .cta-wrapper {
        flex-direction: column;
      }
    }
  }
  h3 {
    font-size: 16px;
    color: inherit;
  }
  & .orange-text-on-white-background {
    width: 200px;
    height: 34px;
    border-radius: 5px;
    margin: 0 0 0 25px;
    background-color: ${themeGet('colors.white')};
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${themeGet('colors.orange')};
    transition: background-color 0.3s ease;
    h3 {
      font-weight: 600;
    }
    &:visited,
    &:hover {
      background-color: #fff8df;
    }
    @media (max-width: ${infoBarBreakpoint}px) {
      margin: 8px 0;
    }
  }
`;

export const MobileNav = styled(
  ({ className, loginLink, loginText, navCta }) => {
    const { setState } = useContext(Context);

    // We force the blade menu to be closed when we go from mobile -> desktop
    let mediaQuery;
    const matchMedia = (breakpoint = mediaQuery) => {
      if (!breakpoint.matches) {
        setState(false);
      }
    };
    useEffect(() => {
      if (typeof window !== 'undefined') {
        mediaQuery = window.matchMedia(
          `(max-width: ${themeGet('breakpoints.ml')})`
        );
        mediaQuery.addListener(matchMedia);
        matchMedia();
      }
      return () => {
        mediaQuery.removeListener(matchMedia);
      };
    }, []);

    return (
      <div className={className}>
        {(!navCta || navCta.showLoginButton !== false) && (
          <Login
            to={loginLink.url}
            name="login"
            ariaLabel={loginLink.ariaLabelLogin}
            queryString={loginLink.queryStringLogin}
            font="Work Sans"
          >
            {loginText}
          </Login>
        )}
        <MenuButton
          onClick={() => setState(true)}
          name="menu"
          aria-label="open the menu"
        />
      </div>
    );
  }
)`
  && {
    @media (min-width: 961px) {
      display: none;
    }
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
`;

MobileNav.propTypes = {
  loginLink: PropTypes.shape().isRequired,
  loginText: PropTypes.string.isRequired,
};

export const DesktopNav = styled(
  ({
    className,
    menu,
    openSections,
    onClick,
    loginLink,
    loginText,
    playLink,
    playText,
    resetState,
    navCta,
    lang,
  }) => (
    <div className={className}>
      <Menu className="no-bullets">
        {menu &&
          menu.map((info, index) => (
            <li key={`mobile-menu-item-${index.toString()}`}>
              <MenuItem
                data={info}
                isOpen={!!openSections[info.text]}
                label={info.text}
                ariaLabel={info.text}
                onClick={onClick}
                resetState={resetState}
                font="Work Sans"
                lang={lang}
              />
            </li>
          ))}
      </Menu>
      {(!navCta || navCta.showLoginButton !== false) && (
        <Login
          to={loginLink.url}
          name="login"
          ariaLabel={loginLink.ariaLabelLogin}
          queryString={loginLink.queryStringLogin}
          font="Work Sans"
        >
          {loginText}
        </Login>
      )}
      {navCta && navCta.headerNavCtaUrl !== undefined ? (
        <Play
          to={navCta.headerNavCtaLink.url}
          ariaLabel={playLink.ariaLabelPlay}
          queryString={playLink.queryStringPlay}
          className={navCta.headerNavCtaButtonStyle}
          font="Work Sans"
          name="play"
        >
          {navCta.headerNavCtaButtonText}
        </Play>
      ) : (
        <Play
          to={playLink.url}
          name="play"
          ariaLabel={playLink.ariaLabelPlay}
          queryString={playLink.queryStringPlay}
          className="solid-on-light-bg"
          font="Work Sans"
        >
          {playText}
        </Play>
      )}
    </div>
  )
)`
  && {
    width: 100%;
    display: flex;
    @media (max-width: ${themeGet('breakpoints.ml')}) {
      display: none;
    }
  }
  & li > div {
    margin: 0 10px;
    }
  }
  & li > div.slideout {
    margin: 0;
    }
  }
`;

DesktopNav.propTypes = {
  menu: PropTypes.arrayOf(PropTypes.shape()),
  openSections: PropTypes.shape(),
  onClick: PropTypes.func,
  loginLink: PropTypes.shape(),
  loginText: PropTypes.string,
  playLink: PropTypes.shape(),
  playText: PropTypes.string,
  lang: PropTypes.string,
};
